import PropTypes from "prop-types";
import React from "react";
import ModalView from "./view";

export const Context = React.createContext({
	onCancel: () => {},
	open: false,
	onValidate: () => {}
});
/** ControlledModal is a controller component that renders a nacre/modal.Modal
 * view. It controls the opening of the modal by the click on the trigger
 * component.
 *
 * When validating or canceling, it calls respectively onValidate and onCancel
 * and closes the modal after it resolves.
 *
 * onCancel and onValidate are both optional. If they are missing both, the
 * view will only render the cancel button as both the validate and cancel
 * button would have both the same effect. If at least one is given, both
 * buttons are rendered.
 *
 * onClose is optional. It is called when the modal is closed, both when it is
 * validated or cancelled.
 */
export default function ControlledModal({
	defaultOpen,
	onValidate,
	onCancel,
	onClose,
	openable,
	...props
}) {
	// opened state, it starts to the defaultOpen prop or false
	const [isOpen, setOpen] = React.useState(Boolean(defaultOpen));
	// onOpen handler on the view, set the opened state to true
	const open = React.useCallback(() => setOpen(true) , [setOpen]);
	// onClose is called when the modal closes, both when validated and cancelled
	const close = React.useCallback(() =>{
		setOpen(false);
		if (onClose) {
			onClose();
		}
	}, [setOpen, onClose]);

	// onValidate handler. calls the prop onValidate and closes the modal after
	// resolution
	const onValidate_ = React.useCallback(async (...args) => {
		if(onValidate) {
			await onValidate(...args);
		}
		close();
	}, [onValidate, close]);

	// onCancel handler. calls the prop onCancel and closes the modal after
	// resolution
	const onCancel_ = React.useCallback(async (...args) => {
		if (onCancel) {
			await onCancel(...args);
		}
		close();
	}, [onCancel, close]);

	return <Context.Provider value={
		{
			onCancel: onCancel_,
			open: isOpen,
			onValidate: onValidate_,
		} }>
		<ModalView
			{ ...props }
			open={ isOpen }
			onCancel={ onCancel_ }
			onOpen={ openable ? open : null }
			onValidate={ (onCancel || onValidate) ? onValidate_ : null }
		/>
	</Context.Provider>;
}

ControlledModal.propTypes = {
	onCancel: PropTypes.func,
	onValidate: PropTypes.func,
	onClose: PropTypes.func,
	defaultOpen: PropTypes.bool,
	openable: PropTypes.bool
};

ControlledModal.defaultProps = {
	openable: true
};

